import { useState } from 'react';
import { createPortal } from 'react-dom';
import { BellRing, X, Smartphone } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { usePushSubscription } from '@/lib/pwa/usePushSubscription';

const STORAGE_KEY = 'team-push-prompt-seen';

export function FirstVisitPushPrompt() {
  const { user } = useAuth();
  const { permission, isSubscribed, subscribe, isLoading } = usePushSubscription();
  const [dismissed, setDismissed] = useState(false);

  // Derive visibility to avoid setState in effect (React Compiler)
  const visible =
    !dismissed &&
    !!user &&
    !isSubscribed &&
    (permission === 'unsupported' || !localStorage.getItem(STORAGE_KEY));

  const handleEnable = () => {
    if (permission === 'default') {
      subscribe();
    }
    localStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
  };

  const handleDismiss = () => {
    // For unsupported, don't set localStorage — show again next session until they add to Home Screen
    if (permission !== 'unsupported') {
      localStorage.setItem(STORAGE_KEY, '1');
    }
    setDismissed(true);
  };

  if (!visible) return null;

  const isUnsupported = permission === 'unsupported';
  const isDenied = permission === 'denied';
  const canEnable = permission === 'default';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="rounded-card bg-surface shadow-overlay relative w-full max-w-sm overflow-hidden">
        <button
          onClick={handleDismiss}
          className="text-sub hover:text-ink-2 rounded-control absolute top-3 left-3 z-10 flex h-10 w-10 items-center justify-center transition-[color,transform] active:scale-[0.96]"
          aria-label="סגור"
        >
          <X size={20} />
        </button>
        <div className="flex flex-col items-center p-6 text-center">
          <div
            className={`rounded-card mb-4 flex h-16 w-16 items-center justify-center ${
              isUnsupported ? 'bg-surface-soft' : isDenied ? 'bg-danger-soft' : 'bg-warn-soft'
            }`}
          >
            {isUnsupported ? (
              <Smartphone className="text-muted h-8 w-8" />
            ) : (
              <BellRing className={`h-8 w-8 ${isDenied ? 'text-danger' : 'text-warn'}`} />
            )}
          </div>

          {isUnsupported && (
            <>
              <h2 className="text-ink mb-2 text-lg font-extrabold text-balance">הוסף למסך הבית</h2>
              <p className="text-muted mb-6 text-sm">
                התראות פוש ומספר ההודעות על האייקון עובדים רק כשהאפליקציה נפתחת מהמסך הראשי. הוסף
                אותה למסך הבית, הפעל התראות, וספירת ההודעות שלא נקראו תוצג על האייקון.
              </p>
              <button
                onClick={handleDismiss}
                className="rounded-control bg-brand hover:bg-brand-ink w-full px-4 py-3 font-bold text-white transition-[background-color,transform] active:scale-[0.96]"
              >
                הבנתי
              </button>
            </>
          )}

          {isDenied && (
            <>
              <h2 className="text-ink mb-2 text-lg font-extrabold text-balance">התראות חסומות</h2>
              <p className="text-muted mb-6 text-sm">
                הפעלת התראות חסומה בהגדרות — גם ספירת ההודעות על האייקון דורשת הרשאת התראות. עבור אל
                הגדרות → Safari → אתר זה → התראות ובדוק שההתראות מאופשרות.
              </p>
              <button
                onClick={handleDismiss}
                className="rounded-control bg-brand hover:bg-brand-ink w-full px-4 py-3 font-bold text-white transition-[background-color,transform] active:scale-[0.96]"
              >
                הבנתי
              </button>
            </>
          )}

          {canEnable && (
            <>
              <h2 className="text-ink mb-2 text-lg font-extrabold text-balance">הפעל התראות</h2>
              <p className="text-muted mb-6 text-sm">
                קבל התראות כשהמנהל שולח הודעות — גם כשהאפליקציה סגורה. מספר ההודעות שלא נקראו יוצג
                על האייקון במסך הבית.
              </p>
              <div className="flex w-full flex-col gap-2">
                <button
                  onClick={handleEnable}
                  disabled={isLoading}
                  className="rounded-control bg-brand hover:bg-brand-ink w-full px-4 py-3 font-bold text-white transition-[background-color,transform] active:scale-[0.96] disabled:opacity-70 disabled:active:scale-100"
                >
                  {isLoading ? 'מפעיל...' : 'הפעל התראות'}
                </button>
                <button
                  onClick={handleDismiss}
                  className="text-muted hover:text-ink-2 min-h-10 w-full py-2 text-sm transition-[color,transform] active:scale-[0.96]"
                >
                  אולי מאוחר יותר
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
